// Control.js
import React, { useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import { UserContext } from "../../../context/UserContext";
import AddCashModal from "../../Elements/AddCashModal";
import { useNavigate, useLocation } from "react-router-dom";
import CustomerModal from "../CustomerComponents/CustomerModal";
import { getImageUrl } from "../../../lib/helpers";
import MemberPhoto from "../../Elements/MemberPhoto";
import { HiOutlinePencilSquare } from "react-icons/hi2";
import { MdCreditCard, MdLocalDrink } from "react-icons/md";
import { GrLogout } from "react-icons/gr";
import { GiHemp } from "react-icons/gi";
import { HiOutlineShoppingCart } from "react-icons/hi";
import { Search, SearchX, EllipsisVertical, CircleUserRound } from "lucide-react";
import axios from 'axios';
import EntryTimer from "./EntryTimer";
import { useInVenueUsers } from "../../../context/useInVenueUsers";
import Dispenser from "../DispenserComponents/Dispenser";
import EnviarABarra from "../BarraComponents/EnviarABarra";
import { VENUE_TYPES } from "../../../lib/consts/venueTypes";

// ─── helpers ─────────────────────────────────────────────────────────────────

function calcElapsedMins(entryTime) {
  if (!entryTime) return 0;
  const t =
    typeof entryTime === "string"
      ? new Date(entryTime.endsWith("Z") ? entryTime : entryTime + "Z")
      : new Date(entryTime);
  if (isNaN(t.getTime())) return 0;
  return Math.max(0, Math.floor((Date.now() - t.getTime()) / 60000));
}

function ElapsedCell({ entryTime }) {
  const [mins, setMins] = useState(() => calcElapsedMins(entryTime));
  useEffect(() => {
    const id = setInterval(() => setMins(calcElapsedMins(entryTime)), 15000);
    return () => clearInterval(id);
  }, [entryTime]);
  return <span className="text-xs text-[#0A0A0A]">{mins} min</span>;
}

function ExitStatusCell({ entryTime }) {
  const [mins, setMins] = useState(() => calcElapsedMins(entryTime));
  useEffect(() => {
    const id = setInterval(() => setMins(calcElapsedMins(entryTime)), 15000);
    return () => clearInterval(id);
  }, [entryTime]);
  const canLeave = mins >= 20;
  return canLeave ? (
    <span className="inline-flex items-center gap-1.5 text-xs text-green-700">
      <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
      Puede salir
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 text-xs text-[#0A0A0A]">
      <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />
      {"< 20 min"}
    </span>
  );
}

// ─── main component ───────────────────────────────────────────────────────────

export default function Control() {
  const [token, , , venueID, venueName, , , , , , , venueType] = useContext(UserContext);
  const [clients, setClients] = useState([]);
  const [id, setId] = useState(null);
  const [activeModal, setActiveModal] = useState(false);
  const [user_id, setUser_id] = useState(null);
  const [creditModal, setCreditModal] = useState(false);
  const [showNuevaEntradaModal, setShowNuevaEntradaModal] = useState(false);
  const [searchFieldNueva, setSearchFieldNueva] = useState("");
  const [nuevaResults, setNuevaResults] = useState([]);
  const [nuevaSearching, setNuevaSearching] = useState(false);
  const nuevaSearchTimer = useRef(null);
  const [sociosSearch, setSociosSearch] = useState("");
  const [draftTotalsByUser, setDraftTotalsByUser] = useState({});
  const [entriesPanelUserId, setEntriesPanelUserId] = useState(null);
  const [notesPanelOpen, setNotesPanelOpen] = useState(false);
  // Social club (flujo de Iván): tocar al socio = seleccionarlo para añadirle
  // productos. Las notas NO se auto-abren (secuestraban la interacción); el
  // toggle "abrir al interactuar" sigue ahí para quien lo prefiera.
  const [autoOpenNotesPanel, setAutoOpenNotesPanel] = useState(
    venueType !== VENUE_TYPES.SOCIAL_CLUB
  );
  const [notesDraft, setNotesDraft] = useState("");
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesError, setNotesError] = useState("");
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
  const isSocialClub = venueType === VENUE_TYPES.SOCIAL_CLUB;

  const navigate = useNavigate();
  const location = useLocation();
  const productosPanelRef = useRef(null);

  const {
    usersInVenue: usersShop,
    markUserEntered,
    markUserLeft,
    refresh: refreshUsersShop,
    updateUserCredit,
    updateUserImage,
    updateUser,
  } = useInVenueUsers(token, venueID);

  const entriesPanelSyncClient = useMemo(() => {
    if (!entriesPanelUserId) return null;
    return usersShop.find((u) => String(u.id) === String(entriesPanelUserId)) ?? null;
  }, [entriesPanelUserId, usersShop]);

  const clientToEdit = useMemo(() => {
    if (!id) return null;
    return (
      usersShop.find((u) => String(u.id) === String(id)) ??
      clients.find((u) => String(u.id) === String(id)) ??
      null
    );
  }, [id, usersShop, clients]);

  // Abrir el perfil del socio = ir directo al modal "Editar socio" (muestra el
  // descuento y todos sus datos). Reusa el flujo de edición de Control.
  const openSocioPerfil = useCallback((userId) => {
    setId(userId);
    setActiveModal(true);
  }, []);

  const selectedClientName = entriesPanelSyncClient
    ? `${entriesPanelSyncClient.profile?.first_name || ""} ${entriesPanelSyncClient.profile?.last_name || ""}`.trim() || entriesPanelSyncClient.email
    : "";

  useEffect(() => {
    setNotesDraft(entriesPanelSyncClient?.profile?.notes || "");
    setNotesError("");
    setNotesSaving(false);
  }, [entriesPanelSyncClient?.id, entriesPanelSyncClient?.profile?.notes]);

  const selectUserForNotes = useCallback((userId, openPanel = false) => {
    setEntriesPanelUserId(userId);
    if (openPanel && autoOpenNotesPanel) setNotesPanelOpen(true);
  }, [autoOpenNotesPanel]);

  useEffect(() => {
    if (!entriesPanelUserId) return;
    if (!usersShop.some((u) => String(u.id) === String(entriesPanelUserId))) {
      setEntriesPanelUserId(null);
      setNotesPanelOpen(false);
    }
  }, [entriesPanelUserId, usersShop]);

  useEffect(() => {
    if (!isSocialClub) return;
    const uid = new URLSearchParams(location.search).get("userId");
    if (uid) setEntriesPanelUserId(uid);
  }, [isSocialClub, location.search]);

  const clearEntriesProductosPanel = useCallback(() => {
    setEntriesPanelUserId(null);
    const p = new URLSearchParams(location.search);
    if (!p.has("userId")) return;
    p.delete("userId");
    const qs = p.toString();
    navigate(`${location.pathname}${qs ? `?${qs}` : ""}`, { replace: true });
  }, [location.pathname, location.search, navigate]);

  const goToProductosOnControl = useCallback(
    (userId) => {
      const p = new URLSearchParams(location.search);
      p.set("panel", "productos");
      if (userId != null && userId !== "") p.set("userId", String(userId));
      navigate(`/control?${p.toString()}`, { replace: true });
    },
    [location.search, navigate]
  );

  useEffect(() => {
    if (!isSocialClub) return;
    if (new URLSearchParams(location.search).get("panel") !== "productos") return;
    const id = requestAnimationFrame(() => {
      productosPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => cancelAnimationFrame(id);
  }, [isSocialClub, location.search]);

  const openActionsMenu = (e, userId) => {
    if (openMenuId === userId) { setOpenMenuId(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    setOpenMenuId(userId);
  };

  // Close actions dropdown on outside click or scroll
  useEffect(() => {
    if (!openMenuId) return;
    const close = (e) => {
      if (!e.target.closest("[data-actions-menu]")) setOpenMenuId(null);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("scroll", close, true);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("scroll", close, true);
    };
  }, [openMenuId]);

  const getClients = useCallback(async () => {
    if (!venueID) return;
    try {
      const response = await axios.get("/v1/venue-users-for-entry", {
        params: { venue_id: venueID },
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const list = response.data || [];
      setClients(list.filter((u) => (u.access_privileges || "").toLowerCase() !== "superadmin"));
    } catch (error) {
      console.error("❌ Error fetching clients:", error);
    }
  }, [token, venueID]);

  const searchNuevaEntrada = useCallback(async (term) => {
    const q = (term || "").trim();
    if (!q || !venueID) {
      setNuevaResults([]);
      setNuevaSearching(false);
      return;
    }
    try {
      const res = await axios.get(`/v1/clients/search/${venueID}`, {
        params: { q, limit: 50 },
        headers: { Authorization: `Bearer ${token}` },
      });
      setNuevaResults(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error("❌ Error searching socios:", error);
      setNuevaResults([]);
    } finally {
      setNuevaSearching(false);
    }
  }, [token, venueID]);

  const handleNuevaSearchChange = (e) => {
    const val = e.target.value;
    setSearchFieldNueva(val);
    clearTimeout(nuevaSearchTimer.current);
    if (!val.trim()) {
      setNuevaResults([]);
      setNuevaSearching(false);
      return;
    }
    setNuevaSearching(true);
    nuevaSearchTimer.current = setTimeout(() => searchNuevaEntrada(val), 350);
  };

  const resetNuevaEntradaSearch = useCallback(() => {
    clearTimeout(nuevaSearchTimer.current);
    setSearchFieldNueva("");
    setNuevaResults([]);
    setNuevaSearching(false);
  }, []);

  const afterVenueCreditBalanceChange = useCallback(
    async (updatedUserId, newBalance) => {
      const creditPatch =
        updatedUserId != null && newBalance !== undefined
          ? { userId: updatedUserId, balance: newBalance }
          : undefined;

      if (creditPatch) {
        updateUserCredit(updatedUserId, newBalance);
        setClients((prev) =>
          prev.map((c) => {
            if (String(c.id) !== String(updatedUserId)) return c;
            const balNum = Number.isFinite(Number(newBalance)) ? Number(newBalance) : 0;
            return {
              ...c,
              profile: {
                ...(c.profile || {}),
                credit_balance: balNum,
                cash_balance: balNum,
              },
            };
          })
        );
      }

      await Promise.all([refreshUsersShop({ creditPatch }), getClients()]);

      if (!isSocialClub || updatedUserId == null) return;
      if (String(entriesPanelUserId) === String(updatedUserId)) {
        clearEntriesProductosPanel();
      }
    },
    [isSocialClub, entriesPanelUserId, updateUserCredit, refreshUsersShop, getClients, clearEntriesProductosPanel]
  );

  const fetchDraftTotals = useCallback(async () => {
    if (!venueID || !token) return;
    try {
      const res = await axios.get("/v1/venue-draft-carts", {
        params: { venue_id: venueID },
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = res.data?.draft_carts || [];
      const byUser = {};
      for (const row of list) {
        const uid = row.user_id;
        if (!byUser[uid]) byUser[uid] = 0;
        byUser[uid] += Number(row.total) || 0;
      }
      setDraftTotalsByUser(byUser);
    } catch (e) {
      console.warn("Fetch venue draft carts failed:", e);
    }
  }, [token, venueID]);

  useEffect(() => {
    fetchDraftTotals();
    const t = setInterval(fetchDraftTotals, 15000);
    return () => clearInterval(t);
  }, [fetchDraftTotals]);

  const goToProductosOnControlWithRefresh = useCallback(
    (userId) => {
      goToProductosOnControl(userId);
      fetchDraftTotals();
    },
    [goToProductosOnControl, fetchDraftTotals]
  );

  const handleCheckout = async (userId) => {
    const user = usersShop.find((u) => u.id === userId);
    if (!user) return;

    const totalOwed = draftTotalsByUser[userId];
    if (totalOwed != null && totalOwed > 0) {
      alert("Este socio tiene productos en el carrito. Finaliza la compra en Dispensario/Productos o en Carritos antes de sacarlo.");
      return;
    }

    if (user.entry_time) {
      const entryTime = new Date(user.entry_time);
      const now = new Date();
      const minutes = Math.floor((now - entryTime) / 60000);
      if (20 - minutes > 0 && !window.confirm(`Este socio ha estado solo ${minutes} min.\n¿Deseas sacarlo?`)) return;
    }

    try {
      await axios.patch("/v1/checkout", null, {
        params: { user_id: userId },
        headers: { Authorization: `Bearer ${token}` },
      });
      markUserLeft(userId);
      setOpenMenuId(null);
    } catch (err) {
      console.error("Error checking out:", err);
    }
  };

  const saveSelectedClientNotes = useCallback(async (nextNotes) => {
    if (!entriesPanelSyncClient?.id || !token) return;
    const notesToSave = nextNotes !== undefined ? nextNotes : notesDraft;
    setNotesSaving(true);
    setNotesError("");
    try {
      const formData = new FormData();
      formData.append(
        "client_data",
        JSON.stringify({ profile: { notes: notesToSave } })
      );
      const res = await fetch(`/v1/clients/${entriesPanelSyncClient.id}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || "No se pudieron guardar las notas");
      }
      const updated = await res.json().catch(() => null);
      if (updated?.id) {
        updateUser(updated.id, updated);
      } else {
        updateUser(entriesPanelSyncClient.id, {
          profile: { ...(entriesPanelSyncClient.profile || {}), notes: notesToSave },
        });
      }
    } catch (e) {
      setNotesError(e?.message || "Error al guardar notas");
    } finally {
      setNotesSaving(false);
    }
  }, [entriesPanelSyncClient, notesDraft, token, updateUser]);

  const handleModal = () => {
    setId(null);
    setActiveModal((v) => !v);
    getClients();
  };

  const handleBalanceModal = (userId) => {
    if (userId) {
      setUser_id(userId);
      setCreditModal(true);
    } else {
      setCreditModal(false);
    }
  };

  const onCashAdded = async (updatedUserId, newBalance) => {
    afterVenueCreditBalanceChange(updatedUserId, newBalance);
  };

  const handleNuevaEntrada = async (clientId) => {
    const client =
      nuevaResults.find((u) => u.id === clientId) ||
      clients.find((u) => u.id === clientId);
    if (!client || !venueID) return;
    try {
      await axios.post(
        "/v1/entries",
        { user_id: client.id, venue_id: venueID },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      const imagePath = client.profile?.image_path || client.profile?.documents_path || "";
      markUserEntered({
        ...client,
        entry_time: new Date(),
        profile: { ...client.profile, image_path: imagePath },
      });
      resetNuevaEntradaSearch();
      setShowNuevaEntradaModal(false);
    } catch (error) {
      console.error("Error creating entry:", error);
    }
  };

  const handleAutoEntryForNewClient = async (newClient) => {
    if (!newClient || !venueID) return;
    try {
      await axios.post(
        "/v1/entries",
        { user_id: newClient.id, venue_id: venueID },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      const imagePath = newClient.profile?.image_path || newClient.profile?.documents_path || "";
      markUserEntered({
        ...newClient,
        entry_time: new Date(),
        profile: { ...newClient.profile, image_path: imagePath },
      });
      await getClients();
    } catch (error) {
      console.error("Error creando entrada automática para nuevo socio:", error);
    }
  };

  const handleClientUpdated = async (updatedClient) => {
    if (!updatedClient || !updatedClient.id) return;
    if (usersShop.some((u) => u.id === updatedClient.id)) {
      const imagePath = updatedClient.profile?.image_path || updatedClient.profile?.documents_path;
      if (imagePath) updateUserImage(updatedClient.id, imagePath);
      updateUser(updatedClient.id, {
        email: updatedClient.email,
        profile: {
          ...updatedClient.profile,
          image_path: imagePath || updatedClient.profile?.image_path,
        },
      });
    }
  };

  const filteredSocios = usersShop.filter((u) => {
    if (!sociosSearch.trim()) return true;
    const q = sociosSearch.trim().toLowerCase();
    const email = u.email?.toLowerCase() || "";
    const firstName = u.profile?.first_name?.toLowerCase() || "";
    const lastName = u.profile?.last_name?.toLowerCase() || "";
    const fullName = `${firstName} ${lastName}`.trim();
    const membershipId = u.membership_id != null ? String(u.membership_id).toLowerCase() : "";
    const legacyId = u.legacy_member_id ? String(u.legacy_member_id).toLowerCase() : "";
    return (
      email.includes(q) ||
      fullName.includes(q) ||
      (membershipId && membershipId.includes(q)) ||
      (legacyId && legacyId.includes(q))
    );
  });

  useEffect(() => {
    if (showNuevaEntradaModal) getClients();
  }, [showNuevaEntradaModal, getClients]);

  // ─── render ───────────────────────────────────────────────────────────────

  return (
    <div className="p-3 sm:p-4 space-y-3 sm:space-y-4 max-w-full overflow-x-hidden">
      <CustomerModal
        active={activeModal}
        handleModal={handleModal}
        token={token}
        id={id}
        clientData={clientToEdit}
        shopName={venueName}
        shopId={venueID}
        setErrorMessage={() => {}}
        onClientCreated={handleAutoEntryForNewClient}
        onClientUpdated={handleClientUpdated}
      />
      <AddCashModal
        active={creditModal}
        handleAddCashModal={() => handleBalanceModal(null)}
        venueID={venueID}
        token={token}
        user_id={user_id}
        setErrorMessage={() => {}}
        onCashAdded={onCashAdded}
        currentBalance={usersShop.find((u) => String(u.id) === String(user_id))?.profile?.credit_balance ?? 0}
        memberName={(() => {
          const m = usersShop.find((u) => String(u.id) === String(user_id));
          const n = `${m?.profile?.first_name || ''} ${m?.profile?.last_name || ''}`.trim();
          return n || undefined;
        })()}
        memberNumber={usersShop.find((u) => String(u.id) === String(user_id))?.membership_id ?? undefined}
        memberStatus="Activo"
        memberImageUrl={(() => {
          const m = usersShop.find((u) => String(u.id) === String(user_id));
          const path = m?.profile?.documents_path || m?.profile?.image_path;
          return path ? getImageUrl(path) : undefined;
        })()}
      />

      <div className="bg-white rounded-xl border border-[#E5E5E5]">

        {/* ── Top bar (sticky en tablet/móvil para que Nueva entrada siga visible al hacer scroll) ── */}
        <div className="sticky top-0 z-30 flex flex-col gap-3 bg-white p-4 border-b border-[#E5E5E5] rounded-t-xl lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 items-center gap-3">
            <h1 className="text-xl font-semibold text-[#0A0A0A] sm:text-2xl truncate">Socios en sala</h1>
            <span className="inline-flex shrink-0 items-center gap-1.5 px-3 py-1 bg-[#F5F5F5] rounded-full text-sm font-medium text-[#0A0A0A] whitespace-nowrap">
              <CircleUserRound size={14} />
              {usersShop.length} dentro
            </span>
          </div>
          <div className="flex w-full min-w-0 flex-col gap-2 sm:flex-row sm:items-center lg:w-auto">
            <div className="relative min-w-0 flex-1 sm:max-w-xs lg:max-w-none">
              <Search
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#737373] pointer-events-none"
                size={14}
              />
              <input
                type="text"
                value={sociosSearch}
                onChange={(e) => setSociosSearch(e.target.value)}
                placeholder="Buscar socio"
                className="h-9 w-full min-w-0 rounded-md border border-[#E5E5E5] pl-8 pr-3 text-sm text-[#0A0A0A] placeholder:text-[#737373] focus:outline-none focus:ring-1 focus:ring-[#0A0A0A] lg:w-64"
              />
            </div>
            <button
              onClick={() => setShowNuevaEntradaModal(true)}
              className="h-9 w-full shrink-0 rounded-md bg-[#171717] px-4 text-sm font-medium text-white hover:bg-black transition-colors whitespace-nowrap sm:w-auto"
            >
              Nueva entrada
            </button>
          </div>
        </div>

        {/* ── Social club: productos panel ─────────────────────── */}
        {isSocialClub && (
          <div ref={productosPanelRef} className="scroll-mt-4 border-b border-[#E5E5E5]">
            <Dispenser
              embedProductosInControl
              entriesPanelUserId={entriesPanelUserId}
              entriesPanelSyncClient={entriesPanelSyncClient}
              entriesPanelUsersInClub={usersShop}
              onEntriesEmbedClearClient={clearEntriesProductosPanel}
              onEmbedClientSelected={(id) => selectUserForNotes(id, true)}
              onEmbeddedVenueCreditSync={afterVenueCreditBalanceChange}
              onEmbeddedDraftCartSynced={fetchDraftTotals}
            />
            {/* CRUO F1: el staff manda el carrito del socio a la pantalla de Barra */}
            {entriesPanelUserId && (
              <EnviarABarra
                token={token}
                venueID={venueID}
                socioId={entriesPanelUserId}
                socioName={entriesPanelSyncClient?.profile?.first_name || null}
                onSent={() => fetchDraftTotals && fetchDraftTotals()}
              />
            )}
          </div>
        )}

        {/* ── Mobile cards (< lg) ──────────────────────────────── */}
        <div className="block lg:hidden p-3 space-y-3">
          {filteredSocios.length === 0 ? (
            <div className="py-10 text-center text-sm text-[#737373]">
              No hay socios en sala.
            </div>
          ) : (
            filteredSocios.map((u) => {
              const displayName =
                u.profile?.first_name && u.profile?.last_name
                  ? `${u.profile.first_name} ${u.profile.last_name}`
                  : u.email || "Socio";
              const balance = Number(u.profile?.credit_balance ?? u.profile?.cash_balance) || 0;
              // Saldo PROYECTADO = saldo real − carrito abierto del socio (preview en vivo).
              const balanceProjected = balance - (Number(draftTotalsByUser[u.id]) || 0);
              return (
                <div
                  key={u.id}
                  className={`bg-white rounded-xl border border-[#E5E5E5] p-4 transition-all ${
                    entriesPanelUserId === u.id ? "ring-2 ring-[#171717]" : ""
                  }`}
                >
                  <div
                    className="flex items-center gap-3 mb-4 cursor-pointer rounded-lg p-1 -m-1 hover:bg-[#F7F7F3]"
                    onClick={() => selectUserForNotes(u.id, true)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        selectUserForNotes(u.id, true);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <MemberPhoto
                      path={u.profile?.documents_path || u.profile?.image_path}
                      name={displayName}
                      className="w-14 h-14 rounded-xl object-cover border border-[#E5E5E5] flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-[#0A0A0A] truncate">{displayName}</div>
                      <div className="text-xs text-[#737373] mt-0.5">
                        Nº {u.legacy_member_id || u.membership_id}
                      </div>
                      <div className="mt-1.5">
                        {balance > 0 ? (
                          <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-[#F7FEE7] text-[#4D7C0F]">
                            €{balance.toFixed(2)}
                          </span>
                        ) : balance < 0 ? (
                          <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-[#FEF2F2] text-[#DC2626]">
                            €{balance.toFixed(2)}
                          </span>
                        ) : (
                          <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-[#FEF2F2] text-[#DC2626]">
                            Sin saldo
                          </span>
                        )}
                        {draftTotalsByUser[u.id] > 0 && (
                          <div className={`mt-0.5 text-[11px] ${balanceProjected < 0 ? 'text-[#DC2626] font-medium' : 'text-[#737373]'}`}>
                            tras carrito €{balanceProjected.toFixed(2)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs bg-[#F7F7F3] rounded-lg p-3 mb-4">
                    <div className="text-[#737373]">
                      Tiempo en sala
                      <div className="mt-0.5">
                        {u.entry_time ? <ElapsedCell entryTime={u.entry_time} /> : <span className="text-[#0A0A0A]">—</span>}
                      </div>
                    </div>
                    <div className="text-[#737373]">
                      Estado
                      <div className="mt-0.5">
                        {u.entry_time ? <ExitStatusCell entryTime={u.entry_time} /> : <span className="text-[#0A0A0A]">—</span>}
                      </div>
                    </div>
                    <div className="text-[#737373]">
                      H. Entrada
                      <div className="mt-0.5">
                        {u.entry_time ? <EntryTimer entryTime={new Date(u.entry_time)} /> : "—"}
                      </div>
                    </div>
                    <div className="text-[#737373]">
                      A pagar
                      <div className="mt-0.5 font-semibold text-[#0A0A0A]">
                        {draftTotalsByUser[u.id] > 0
                          ? `€${Number(draftTotalsByUser[u.id]).toFixed(2)}`
                          : "—"}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className={isSocialClub ? "grid grid-cols-1 gap-2" : "grid grid-cols-2 gap-2"}>
                      {!isSocialClub && (
                        <button
                          onClick={() => navigate(`/dispenser?userId=${u.id}`)}
                          className="flex items-center justify-center gap-2 px-3 py-2.5 bg-[#171717] hover:bg-black text-white rounded-lg text-sm font-semibold transition-all active:scale-95"
                        >
                          <GiHemp className="text-base" />
                          <span>Dispensario</span>
                        </button>
                      )}
                      <button
                        onClick={() =>
                          isSocialClub
                            ? goToProductosOnControlWithRefresh(u.id)
                            : navigate(`/dispenser?mode=productos&userId=${u.id}`)
                        }
                        className="flex items-center justify-center gap-2 px-3 py-2.5 bg-[#171717] hover:bg-black text-white rounded-lg text-sm font-semibold transition-all active:scale-95"
                      >
                        <MdLocalDrink className="text-base" />
                        <span>Productos</span>
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => handleBalanceModal(u.id)}
                        className="flex items-center justify-center gap-2 px-3 py-2.5 border border-[#E5E5E5] bg-white hover:bg-[#F7F7F3] text-[#0A0A0A] rounded-lg text-sm font-semibold transition-all active:scale-95"
                      >
                        <MdCreditCard className="text-base" />
                        <span>Crédito</span>
                      </button>
                      <button
                        onClick={() => navigate(`/carritos?user_id=${u.id}`)}
                        className="flex items-center justify-center gap-2 px-3 py-2.5 border border-[#E5E5E5] bg-white hover:bg-[#F7F7F3] text-[#0A0A0A] rounded-lg text-sm font-semibold transition-all active:scale-95"
                      >
                        <HiOutlineShoppingCart className="text-base" />
                        <span>Carrito</span>
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => openSocioPerfil(u.id)}
                      className="w-full flex items-center justify-center gap-2 px-3 py-2.5 border border-[#E5E5E5] bg-white hover:bg-[#F7F7F3] text-[#0A0A0A] rounded-lg text-sm font-semibold transition-all active:scale-95"
                    >
                      <CircleUserRound className="text-base" size={16} />
                      <span>Perfil</span>
                    </button>
                    <button
                      onClick={() => handleCheckout(u.id)}
                      className="w-full flex items-center justify-center gap-2 px-3 py-2.5 bg-[#FEF2F2] hover:bg-red-100 text-[#DC2626] rounded-lg text-sm font-semibold transition-all active:scale-95"
                    >
                      <GrLogout className="text-base" />
                      <span>Salir</span>
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* ── Desktop table (≥ lg) ─────────────────────────────── */}
        <div className="hidden lg:block">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-[#E5E5E5]">
                <th className="px-4 py-3 text-left text-sm font-bold text-[#0A0A0A]">Socio</th>
                <th className="px-4 py-3 text-left text-sm font-bold text-[#0A0A0A]">Saldo</th>
                <th className="px-4 py-3 text-left text-sm font-bold text-[#0A0A0A]">Tiempo en sala</th>
                <th className="px-4 py-3 text-left text-sm font-bold text-[#0A0A0A]">Estado de salida</th>
                <th className="px-4 py-3 text-right text-sm font-bold text-[#0A0A0A]">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredSocios.map((u) => {
                const balance = Number(u.profile?.credit_balance ?? u.profile?.cash_balance) || 0;
                // Saldo PROYECTADO = saldo real − carrito abierto del socio (preview en vivo).
                const balanceProjected = balance - (Number(draftTotalsByUser[u.id]) || 0);
                const hasDraftCart = draftTotalsByUser[u.id] > 0;
                return (
                  <tr
                    key={u.id}
                    className={`border-b border-[#E5E5E5] last:border-b-0 hover:bg-[#F7F7F3] transition-colors ${
                      entriesPanelUserId === u.id ? "!bg-[#F5F5F5]" : ""
                    }`}
                  >
                    {/* Socio */}
                    <td
                      className="px-4 py-3 align-middle cursor-pointer"
                      onClick={() => selectUserForNotes(u.id, true)}
                    >
                      <div className="flex items-center gap-3">
                        <MemberPhoto
                          path={u.profile?.documents_path || u.profile?.image_path}
                          name={`${u.profile?.first_name || ''} ${u.profile?.last_name || ''}`.trim()}
                          className="w-10 h-10 rounded-lg object-cover border border-[#E5E5E5] flex-shrink-0"
                        />
                        <div>
                          <div className="font-medium text-[#0A0A0A]">
                            {u.profile?.first_name} {u.profile?.last_name}
                          </div>
                          <div className="text-xs text-[#737373]">
                            Nº {u.legacy_member_id || u.membership_id}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Saldo (real) + efecto del carrito como línea secundaria */}
                    <td className="px-4 py-3 align-middle">
                      {balance > 0 ? (
                        <span className="inline-block px-2.5 py-1 rounded-xl text-xs font-medium bg-[#F7FEE7] text-[#4D7C0F]">
                          €{balance.toFixed(2)}
                        </span>
                      ) : balance < 0 ? (
                        <span className="inline-block px-2.5 py-1 rounded-xl text-xs font-medium bg-[#FEF2F2] text-[#DC2626]">
                          €{balance.toFixed(2)}
                        </span>
                      ) : (
                        <span className="inline-block px-2.5 py-1 rounded-xl text-xs font-medium bg-[#FEF2F2] text-[#DC2626]">
                          Sin saldo
                        </span>
                      )}
                      {draftTotalsByUser[u.id] > 0 && (
                        <div className={`mt-0.5 text-[11px] ${balanceProjected < 0 ? 'text-[#DC2626] font-medium' : 'text-[#737373]'}`}>
                          tras carrito €{balanceProjected.toFixed(2)}
                        </div>
                      )}
                    </td>

                    {/* Tiempo en sala */}
                    <td className="px-4 py-3 align-middle">
                      {u.entry_time
                        ? <ElapsedCell entryTime={u.entry_time} />
                        : <span className="text-[#737373]">—</span>}
                    </td>

                    {/* Estado de salida */}
                    <td className="px-4 py-3 align-middle">
                      {u.entry_time
                        ? <ExitStatusCell entryTime={u.entry_time} />
                        : <span className="text-[#737373]">—</span>}
                    </td>

                    {/* Acciones */}
                    <td className="px-4 py-3 align-middle text-right">
                      <div className="inline-block" data-actions-menu="">
                        <button
                          onClick={(e) => openActionsMenu(e, u.id)}
                          className="p-1.5 rounded-md border border-[#E5E5E5] hover:bg-[#F5F5F5] transition-colors text-[#0A0A0A]"
                          title="Acciones"
                        >
                          <EllipsisVertical size={16} />
                        </button>

                        {openMenuId === u.id && (
                          <div
                            data-actions-menu=""
                            style={{ top: menuPos.top, right: menuPos.right }}
                            className="fixed z-50 min-w-[176px] bg-white border border-[#E5E5E5] rounded-lg shadow-lg overflow-hidden"
                          >
                            {!isSocialClub && (
                              <button
                                className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-[#0A0A0A] hover:bg-lime-400 transition-colors"
                                onClick={() => { setOpenMenuId(null); navigate(`/dispenser?userId=${u.id}`); }}
                              >
                                <GiHemp size={14} />
                                Dispensario
                              </button>
                            )}
                            <button
                              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-[#0A0A0A] hover:bg-lime-400 transition-colors"
                              onClick={() => {
                                setOpenMenuId(null);
                                isSocialClub
                                  ? goToProductosOnControlWithRefresh(u.id)
                                  : navigate(`/dispenser?mode=productos&userId=${u.id}`);
                              }}
                            >
                              <MdLocalDrink size={14} />
                              Barra
                            </button>
                            <button
                              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-[#0A0A0A] hover:bg-lime-400 transition-colors"
                              onClick={() => { setOpenMenuId(null); handleBalanceModal(u.id); }}
                            >
                              <MdCreditCard size={14} />
                              Saldo
                              {hasDraftCart && (
                                <span className="ml-auto text-xs text-[#737373]">
                                  €{Number(draftTotalsByUser[u.id]).toFixed(2)}
                                </span>
                              )}
                            </button>
                            <button
                              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-[#0A0A0A] hover:bg-lime-400 transition-colors"
                              onClick={() => { setOpenMenuId(null); navigate(`/carritos?user_id=${u.id}`); }}
                            >
                              <HiOutlineShoppingCart size={14} />
                              Carrito
                            </button>
                            <div className="border-t border-[#E5E5E5]" />
                            <button
                              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-[#0A0A0A] hover:bg-lime-400 transition-colors"
                              onClick={() => { setOpenMenuId(null); openSocioPerfil(u.id); }}
                            >
                              <CircleUserRound size={14} />
                              Perfil
                            </button>
                            <button
                              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-[#0A0A0A] hover:bg-lime-400 transition-colors"
                              onClick={() => { setOpenMenuId(null); selectUserForNotes(u.id); setNotesPanelOpen(true); }}
                            >
                              <HiOutlinePencilSquare size={14} />
                              Notas
                            </button>
                            <button
                              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-[#DC2626] hover:bg-[#FEF2F2] transition-colors"
                              onClick={() => handleCheckout(u.id)}
                            >
                              <GrLogout size={14} />
                              Salida
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filteredSocios.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-sm text-[#737373]">
                    No hay socios en sala.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Notes panel (mismo diseño; visible también en tablet vertical < lg) ── */}
      <div className="block">
        <div
          className={`fixed right-0 top-1/2 z-40 -translate-y-1/2 transition-transform duration-300 max-lg:top-[42%] ${
            notesPanelOpen ? "translate-x-0" : "translate-x-[calc(100%-56px)]"
          }`}
        >
          <div className="flex items-start">
            <button
              type="button"
              onClick={() => setNotesPanelOpen((v) => !v)}
              className="mt-12 flex h-20 w-12 flex-col items-center justify-center gap-1 rounded-l-[1rem] border border-lime-300 bg-lime-500 px-1 text-center text-[10px] font-semibold text-white shadow-lg transition hover:bg-lime-600 max-lg:mt-8 lg:mt-20"
              title={notesPanelOpen ? "Ocultar notas" : "Mostrar notas"}
            >
              <HiOutlinePencilSquare className="text-lg" />
              <span>Notas</span>
            </button>

            <aside className="w-[min(360px,calc(100vw-3.5rem))] max-h-[min(520px,calc(100dvh-5rem))] overflow-y-auto rounded-l-3xl border border-lime-200 bg-lime-50 shadow-2xl">
              <div className="flex items-start justify-between gap-3 border-b border-lime-200 bg-lime-500/10 px-5 py-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Cuadro de notas</h2>
                  <p className="mt-1 text-sm text-gray-600">
                    {entriesPanelSyncClient
                      ? selectedClientName
                      : "Identifica primero un socio para ver su cuadro de notas."}
                  </p>
                </div>
                {entriesPanelSyncClient ? (
                  <button
                    type="button"
                    onClick={() => {
                      setId(entriesPanelSyncClient.id);
                      setActiveModal(true);
                    }}
                    className="rounded-lg border border-lime-300 bg-white px-3 py-2 text-sm font-medium text-lime-800 transition hover:border-lime-400 hover:bg-lime-100"
                  >
                    Editar
                  </button>
                ) : null}
              </div>

              <div className="p-4">
                <label className="mb-4 flex items-center gap-3 rounded-2xl border border-lime-200 bg-white px-4 py-3 text-sm text-gray-700">
                  <button
                    type="button"
                    onClick={() => setAutoOpenNotesPanel((v) => !v)}
                    className={`relative h-7 w-12 rounded-full transition ${autoOpenNotesPanel ? "bg-lime-500" : "bg-slate-300"}`}
                    aria-pressed={autoOpenNotesPanel}
                  >
                    <span
                      className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition ${autoOpenNotesPanel ? "left-6" : "left-1"}`}
                    />
                  </button>
                  <span>Abrir al interactuar con el socio</span>
                </label>

                <div className="min-h-[240px] max-lg:min-h-[200px] lg:min-h-[360px] rounded-2xl border border-lime-100 bg-white/90 p-4 flex flex-col gap-3">
                  {notesError ? (
                    <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                      {notesError}
                    </div>
                  ) : null}
                  <textarea
                    value={entriesPanelSyncClient ? notesDraft : ""}
                    onChange={(e) => setNotesDraft(e.target.value)}
                    disabled={!entriesPanelSyncClient || notesSaving}
                    className={`flex-1 w-full resize-none rounded-xl border px-3 py-2 text-sm leading-6 outline-none ${
                      entriesPanelSyncClient
                        ? "border-lime-200 bg-white text-gray-800 focus:ring-2 focus:ring-lime-500"
                        : "border-gray-200 bg-gray-50 text-gray-400"
                    }`}
                    placeholder={
                      entriesPanelSyncClient
                        ? "Escribe notas internas sobre este socio…"
                        : "Selecciona un socio desde la tabla para ver su cuadro de notas."
                    }
                  />
                  <button
                    type="button"
                    onClick={() => saveSelectedClientNotes()}
                    disabled={!entriesPanelSyncClient || notesSaving}
                    className="w-full rounded-xl bg-lime-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-lime-700 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {notesSaving ? "Guardando…" : "Guardar notas"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setNotesDraft("");
                      setNotesError("");
                      saveSelectedClientNotes("");
                    }}
                    disabled={!entriesPanelSyncClient || notesSaving}
                    className="w-full rounded-xl border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-700 transition hover:bg-red-50 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    Borrar notas
                  </button>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>

      {/* ── Nueva Entrada modal ───────────────────────────────── */}
      {showNuevaEntradaModal && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
          onClick={() => {
            setShowNuevaEntradaModal(false);
            resetNuevaEntradaSearch();
          }}
        >
          <div
            className="bg-white rounded-[10px] border border-[#E5E5E5] w-full max-w-[443px] p-6 my-4 max-h-[85vh] overflow-hidden flex flex-col"
            style={{ boxShadow: "0px 4px 6px -4px rgba(0,0,0,0.1), 0px 10px 15px -3px rgba(0,0,0,0.1)" }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-[#0A0A0A]">Nueva entrada</h3>
              <button
                onClick={() => {
                  setShowNuevaEntradaModal(false);
                  resetNuevaEntradaSearch();
                }}
                className="text-[#737373]/70 hover:text-[#0A0A0A] transition-colors"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12"/></svg>
              </button>
            </div>

            {/* Search input */}
            <div className="relative mb-4">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-[#737373] pointer-events-none"
                size={16}
              />
              <input
                type="text"
                value={searchFieldNueva}
                onChange={handleNuevaSearchChange}
                placeholder="Buscar socio"
                className="w-full h-9 rounded-md border border-[#E5E5E5] pl-9 pr-3 text-sm text-[#0A0A0A] placeholder:text-[#737373] focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
                autoFocus
              />
            </div>

            {/* Results */}
            <div className="overflow-y-auto flex-1 min-h-0">
              {!searchFieldNueva.trim() ? (
                <p className="py-8 text-center text-sm text-[#737373]">
                  Empieza a escribir para buscar…
                </p>
              ) : nuevaSearching ? (
                <p className="py-8 text-center text-sm text-[#737373]">Buscando…</p>
              ) : nuevaResults.filter((c) => !usersShop.some((u) => u.id === c.id)).length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-1.5 px-4 py-6 text-center">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#F3F4F6]">
                    <SearchX size={16} className="text-[#737373]" />
                  </span>
                  <p className="text-sm font-medium text-[#0A0A0A]">Sin resultados</p>
                  <p className="text-xs text-[#737373]">No hay socios que coincidan con tu búsqueda</p>
                </div>
              ) : (
                <div>
                  {nuevaResults
                    .filter((c) => !usersShop.some((u) => u.id === c.id))
                    .map((c) => {
                      const balance = Number(c.profile?.credit_balance ?? c.profile?.cash_balance) || 0;
                      return (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => handleNuevaEntrada(c.id)}
                          className="flex w-full items-center gap-3 py-2 border-b border-[#E5E5E5] last:border-b-0 text-left transition-colors hover:bg-[#F7F7F3]"
                        >
                          <MemberPhoto
                            path={c.profile?.documents_path || c.profile?.image_path}
                            name={`${c.profile?.first_name || ''} ${c.profile?.last_name || ''}`.trim()}
                            className="w-[46px] h-[46px] rounded-lg object-cover flex-shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-[#0A0A0A] truncate">
                              {c.profile?.first_name} {c.profile?.last_name}
                            </div>
                            <div className="text-sm text-[#737373] mt-0.5">
                              Nº Socio: {c.legacy_member_id || c.membership_id}
                              {" · "}
                              Crédito: <span className={balance <= 0 ? "text-[#DC2626]" : undefined}>€{balance.toFixed(2)}</span>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
